/**
 * The page's centerpiece: notes that find each other, drawn as a constellation
 * of note cards with the newest one linking itself to two older ones.
 *
 * The research behind the redesign found nobody in the category holding both
 * ends of this claim — the privacy apps link by hand, the AI apps link in the
 * cloud — so this section gets the page's largest visual. The graph is a
 * concept illustration in the student thread the whole page tells, not an app
 * screenshot; the phone in the hero already shows the real surface.
 *
 * Raw DOM `<svg>`, the same deliberate choice as `components/icons/glyph.tsx`:
 * this tree only renders in a browser. Node fills and strokes are audited
 * theme roles — the fresh note is `tertiaryContainer` with `onTertiaryContainer`
 * text, its links are `tertiary`, everything settled is an elevation fill with
 * `onSurface` text — so the WCAG suite already covers every pairing.
 *
 * Static on purpose. The page has never animated and the calm is part of the
 * Vault register; if a draw-in ever feels worth it, it belongs behind
 * `prefers-reduced-motion` and a separate decision.
 *
 * The zettelkasten line is the page's one nod to the tradition, spelled out in
 * plain words. Marketing homepages in this space never use the term (0 of 7
 * surveyed) — one explained mention is the deliberate budget.
 */

import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { MarketingSection } from "@/components/marketing/section";
import { SectionHead } from "@/components/marketing/section-head";
import { SANS_STACK, type AppTheme, useAppTheme } from "@/theme/paper-theme";
import { spacing } from "@/theme/tokens";
import { PROSE_MEASURE } from "@/theme/web-tokens";

const LEDE =
  "Every new note gets checked against everything you've said before. When a fresh thought overlaps an old one, Sparkie links the two. The chapter you talked through last week comes back on its own, the moment a lecture lands on the same idea.";

const ZETTEL_LINE =
  "Note-takers have chased this for a century: the zettelkasten, a box of notes that talk to each other. It only works if someone does the filing. Sparkie does the filing.";

const GRAPH_LABEL =
  "A map of notes with lines connecting related ones. A new lecture note about the spacing effect links itself to a reading note and a study group recap.";

/** One settled note card in the constellation. */
interface GraphNode {
  x: number;
  y: number;
  width: number;
  label: string;
}

const NODE_HEIGHT = 42;

const NODES: readonly GraphNode[] = [
  { x: 95, y: 70, width: 190, label: "Make It Stick — ch. 3" },
  { x: 550, y: 66, width: 170, label: "Study group recap" },
  { x: 46, y: 252, width: 170, label: "Stats midterm plan" },
  { x: 632, y: 246, width: 150, label: "Weekend hike" },
  { x: 178, y: 336, width: 180, label: "Essay — habit loops" },
  { x: 507, y: 334, width: 170, label: "Club budget notes" },
] as const;

const FRESH_NODE = {
  x: 322,
  y: 190,
  width: 196,
  height: 46,
  label: "Psych — spacing effect",
} as const;

/** Older links between settled notes; drawn first, under the cards. */
const QUIET_EDGES: readonly string[] = [
  "M190 100 Q 140 180 128 258",
  "M132 278 Q 190 320 268 348",
  "M640 100 Q 690 170 706 248",
  "M700 270 Q 650 320 596 346",
  "M262 96 Q 420 70 566 88",
] as const;

/** The two links the fresh note just wrote, in the live hue. */
const FRESH_EDGES: readonly string[] = [
  "M382 196 Q 280 150 232 112",
  "M462 194 Q 560 150 604 108",
] as const;

function ConstellationGraph({ theme }: { theme: AppTheme }) {
  return (
    <svg
      viewBox="0 0 860 430"
      role="img"
      aria-label={GRAPH_LABEL}
      style={{
        display: "block",
        width: "100%",
        maxWidth: 860,
        height: "auto",
      }}
    >
      {QUIET_EDGES.map((d) => (
        <path
          key={d}
          d={d}
          fill="none"
          stroke={theme.colors.outline}
          strokeWidth={1.2}
          opacity={0.55}
        />
      ))}
      {FRESH_EDGES.map((d) => (
        <path
          key={d}
          d={d}
          fill="none"
          stroke={theme.colors.tertiary}
          strokeWidth={2}
        />
      ))}

      {NODES.map(({ x, y, width, label }) => (
        <g key={label}>
          <rect
            x={x}
            y={y}
            width={width}
            height={NODE_HEIGHT}
            rx={11}
            fill={theme.colors.elevation.level1}
            stroke={theme.colors.outlineVariant}
          />
          <text
            x={x + width / 2}
            y={y + 26}
            textAnchor="middle"
            fill={theme.colors.onSurface}
            fontSize={13}
            fontFamily={SANS_STACK}
          >
            {label}
          </text>
        </g>
      ))}

      <g>
        <rect
          x={FRESH_NODE.x}
          y={FRESH_NODE.y}
          width={FRESH_NODE.width}
          height={FRESH_NODE.height}
          rx={11}
          fill={theme.colors.tertiaryContainer}
          stroke={theme.colors.tertiary}
          strokeWidth={1.5}
        />
        <text
          x={FRESH_NODE.x + FRESH_NODE.width / 2}
          y={FRESH_NODE.y + 28}
          textAnchor="middle"
          fill={theme.colors.onTertiaryContainer}
          fontSize={13}
          fontWeight={600}
          fontFamily={SANS_STACK}
        >
          {FRESH_NODE.label}
        </text>
      </g>
    </svg>
  );
}

export function SelfLinking() {
  const theme = useAppTheme();

  return (
    <MarketingSection style={styles.section}>
      <SectionHead
        eyebrow="Self-linking notes"
        heading="Notes that find each other."
        lede={LEDE}
      />

      <View style={styles.graph}>
        <ConstellationGraph theme={theme} />
      </View>

      <Text
        variant="titleLarge"
        style={[styles.zettel, { color: theme.colors.onSurfaceVariant }]}
      >
        {ZETTEL_LINE}
      </Text>
    </MarketingSection>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: spacing.xl,
  },
  graph: {
    alignItems: "center",
    width: "100%",
  },
  // Serif and a shade quieter than body ink: an aside in the author's voice,
  // not a claim heading.
  zettel: {
    maxWidth: PROSE_MEASURE,
    alignSelf: "center",
    width: "100%",
    textAlign: "center",
    fontWeight: "400",
  },
});
